import { createRequestSaga, showError, showSuccess } from '@weave/alert-system';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import { getErrorMessage } from '../../../axios';
import { addNewUser } from '../../users';
import { VerticalEnum } from '../../../../models/vertical-enum';
import {
  LocationModel,
  LocationModelWithPhoneData,
  OfficeInfo,
  LocationActions,
  LocationsActionTypes,
  RequestCreateLocationAction,
  RequestUpdateLocationAction,
  RemoveLocationAction,
  createDemoUserFromLocation,
  CreateLocationDTO,
  RequestSetLocationParentAction,
  RequestSetPhoneTenantID,
} from './locations.types';
import {
  createLocationFailure,
  createLocationLoading,
  createLocationSuccess,
  LocationCreationResponseModel,
} from '../location-status';
import { selectLocation } from './locations.selectors';
import { selectCurrentLocationId, unsetCurrentLocationId } from '../current-location';
import { history } from '../../../store/history';
import { clearCachedActions } from '../../cached-actions';
import { ResolvedPromise } from '../../../store/store-utils';
import * as locationApi from './api';

export const requestCreateLocation = (
  location: CreateLocationDTO
): RequestCreateLocationAction => ({
  type: LocationsActionTypes.RequestCreateLocation,
  payload: location,
});
export const requestUpdateLocation = (
  locationId: string,
  location: Partial<LocationModel>,
  onFinish?: () => void
): RequestUpdateLocationAction => ({
  type: LocationsActionTypes.RequestUpdateLocation,
  payload: { locationId, location, onFinish },
});
export const requestSetLocationParent = (
  locationId: string,
  parentLocationId: string
): RequestSetLocationParentAction => ({
  type: LocationsActionTypes.RequestSetLocationParent,
  payload: { locationId, parentLocationId },
});
export const requestSetPhoneTenantID = (
  phoneTenanatId: string
): RequestSetPhoneTenantID => ({
  type: LocationsActionTypes.RequestSetPhoneTenantID,
  payload: { phoneTenanatId },
});
export const addLocations = (locations: LocationModel[]): LocationActions => ({
  type: LocationsActionTypes.AddLocations,
  payload: locations,
});
export const updateLocation = (locationId: string, location: Partial<LocationModel>) => ({
  type: LocationsActionTypes.UpdateLocation,
  payload: { locationId, location },
});
export const removeLocation = (locationId: string): LocationActions => ({
  type: LocationsActionTypes.RemoveLocation,
  payload: locationId,
});

export const handleCreateLocation = createRequestSaga<RequestCreateLocationAction>({
  key: LocationsActionTypes.RequestCreateLocation,
  displayErrors: true,
  onError: (error) => `Location Creation Failure: ${error}`,
  saga: function* (action) {
    try {
      yield put(createLocationLoading());
      const uncreatedLocation = action.payload;
      uncreatedLocation.provisioned_from = 'WAM';
      let location: LocationModelWithPhoneData;
      if (uncreatedLocation.createPhoneTenant) {
        const provisionResponse: ResolvedPromise<
          ReturnType<typeof locationApi.provisionLocation>
        > = yield call(locationApi.provisionLocation, uncreatedLocation);
        location = { ...uncreatedLocation, LocationID: provisionResponse.location_id };
        yield put(createLocationSuccess(provisionResponse));
        if (location.Type === 'Demo' && location.salesforce_products) {
          const user = createDemoUserFromLocation(location);
          yield put(addNewUser(user));
        }
      } else {
        const response: ResolvedPromise<ReturnType<typeof locationApi.createLocation>> =
          yield call(locationApi.createLocation, uncreatedLocation);
        const creationResponse: LocationCreationResponseModel = {
          customization_flags_setup_successfully: false,
          activated_packages_successfully: false,
          e911_setup_successfully: false,
          location_created_successfully: true,
          phone_data_tenant_created_successfully: false,
          software_integration_setup_successfully: false,
          temp_number_setup_successfully: false,
          location_id: response,
          activated_packages_error: '',
          customization_flags_error: '',
          e911_setup_error: '',
          location_created_error: '',
          phone_data_tenant_created_error: '',
          software_integration_error: '',
          temp_number_setup_error: '',
          user_created_error: '',
          user_created_successfully: false,
        };

        location = { ...uncreatedLocation, LocationID: creationResponse.location_id };
        yield put(createLocationSuccess(creationResponse));
      }
    } catch (error: any) {
      yield put(createLocationFailure());
      throw error;
    }
  },
});

export const handleUpdateLocation = createRequestSaga<RequestUpdateLocationAction>({
  key: LocationsActionTypes.RequestUpdateLocation,
  displayErrors: true,
  onError: (error) => getErrorMessage(error) || 'Failed to update location',
  saga: function* ({ payload }) {
    const { locationId, location: changes, onFinish } = payload;
    const prevLocation: ReturnType<ReturnType<typeof selectLocation>> = yield select(
      selectLocation(locationId)
    );
    if (!prevLocation) {
      yield put(showError(`Location ${locationId} not found`));
    }
    const location = {
      ...prevLocation,
      ...changes,
      ...(changes.VerticalID && { Vertical: VerticalEnum[changes.VerticalID] }),
    };
    yield call(locationApi.updateLocation, locationId, location);

    const officeInfo: OfficeInfo = {
      businessName: location.Name,
      businessPhone: location.Phone,
      businessEmail: location.Email,
      timezone: location.Timezone,
      industry: VerticalEnum[location.VerticalID] ?? 'Unknown',
    };

    yield put(updateLocation(locationId, location));
    yield call(locationApi.updateOfficeInfo, officeInfo);
    yield put(showSuccess('Location Updated Successfully'));
    if (onFinish) {
      onFinish();
    }
  },
});

export const handleSetLocationParent = function* (
  action: RequestSetLocationParentAction
) {
  try {
    const { locationId, parentLocationId } = action.payload;
    yield call(locationApi.setLocationParent, locationId, parentLocationId);

    yield put(updateLocation(locationId, { ParentID: parentLocationId }));
    yield put(showSuccess('Location Parentage Updated Successfully'));
  } catch (error: any) {
    yield put(showError(getErrorMessage(error)));
  }
};

export const handleSetPhoneTenantId = function* (action: RequestSetPhoneTenantID) {
  try {
    const locationId: ReturnType<typeof selectCurrentLocationId> = yield select(
      selectCurrentLocationId
    );
    const { phoneTenanatId } = action.payload;
    yield call(locationApi.setPhoneTenantId, locationId, phoneTenanatId);
    yield put(updateLocation(locationId, { PhoneTenantID: phoneTenanatId }));
  } catch (error: any) {
    yield put(showError(getErrorMessage(error)));
  }
};

export const handleRemoveLocation = function* (action: RemoveLocationAction) {
  const locationId: ReturnType<typeof selectCurrentLocationId> = yield select(
    selectCurrentLocationId
  );
  // Todo: eventually we may not want to remove these cached actions when removing a location
  // Will revisit this once our cached actions season a little longer
  yield put(clearCachedActions(action.payload));
  if (locationId === action.payload) {
    yield put(unsetCurrentLocationId());
    history.push('/');
  }
};

export const locationsSaga = function* () {
  yield all([
    yield takeLatest(LocationsActionTypes.RequestCreateLocation, handleCreateLocation),
    yield takeLatest(LocationsActionTypes.RequestUpdateLocation, handleUpdateLocation),
    yield takeLatest(
      LocationsActionTypes.RequestSetLocationParent,
      handleSetLocationParent
    ),
    yield takeLatest(
      LocationsActionTypes.RequestSetPhoneTenantID,
      handleSetPhoneTenantId
    ),
    yield takeLatest(LocationsActionTypes.RemoveLocation, handleRemoveLocation),
  ]);
};
